import { DarkPopup } from '@elements/Popup';
import { HeadingContainer } from '@page-components/timeline/common';
import { ShipmentTypeKeys } from '@utility/constants/common';
import { DateUtility } from '@utility/date';
import { Info, Warning } from '@phosphor-icons/react';
import { useEffect, useMemo } from 'react';
import { Checkbox, cn } from '@skupreme/shared/ui';
import { CommonUtility } from '@utility/common';
import { ShipmentRowRenderer } from './row';

const red = '#FF4218';

export function ShipmentsSection({
  DayWidth,
  setPredictedShipment,
  filteredShipments,
  setFilteredShipments,
  checkEntry,
  differenceInDates,
  setMaxDate,
  setMinDate,
  data,
  checked,
  setChecked,
  showShipmentCheckbox = true,
  hidePOPrediction,
  onlyShipments = false,
  ...props
}) {
  const toggleCheck = (key, value) => {
    const userValue = { ...checked };
    userValue[key].checked = value;
    setChecked(userValue);
  };

  const toggleExpand = (key, value) => {
    const userValue = { ...checked };
    userValue[key].expanded = value;
    setChecked({ ...userValue });
  };

  const allChecked = useMemo(() => {
    for (const supplier_id in checked) {
      if (!checked[supplier_id].checked) return false;
    }
    return true;
  }, [checked]);

  const toggleAll = () => {
    const toSet = !allChecked;
    const userValue = { ...checked };
    for (const _id in userValue) {
      if (Object.prototype.hasOwnProperty.call(userValue, _id)) {
        checked[_id].checked = toSet;
      }
    }
    setChecked(userValue);
  };

  useEffect(() => {
    if (!CommonUtility.isValidObject(data)) {
      setPredictedShipment([]);
      setFilteredShipments([]);
      return;
    }

    let arr = [];

    let minDate = null;
    let maxDate = null;
    const userValue = {};

    const shipments = [];
    const shipmentsArray = [data.shipments, data.predicted_shipments];
    setPredictedShipment(data.predicted_shipments);

    shipmentsArray.forEach(allShipments => {
      for (let i = 0; i < allShipments?.length; i += 1) {
        let optEndDate = allShipments[i].endDate;
        if (allShipments[i].status === 'receiving') {
          if (DateUtility.isDateAfter(new Date(), new Date(optEndDate))) {
            optEndDate = DateUtility.addHours(new Date(), 1);
          }
        }
        const shipment = checkEntry({
          ...allShipments[i],
          endDate: optEndDate,
        });
        if (!shipment) continue;
        if (
          shipment.status === 'prediction' &&
          shipment.origin_type === ShipmentTypeKeys.FP
        ) {
          continue;
        }

        const destination_id = shipment?.destination_details?._id;
        const origin_id = shipment?.origin_details?._id || shipment.origin_id;

        if (shipment.status === 'prediction' && shipment.from_prediction) {
          if (hidePOPrediction) {
            continue;
          }
          const relevantPO = data.order_predictions.find(
            x => x.status === 'prediction' && x.supplier?._id === origin_id,
          );
          if (relevantPO) {
            const daysToShip = DateUtility.difference(
              new Date(shipment.startDate),
              new Date(shipment.endDate),
            );
            shipment.startDate = relevantPO.endDate;
            shipment.endDate = DateUtility.addDays(
              new Date(relevantPO.endDate),
              daysToShip,
            );
          }
        }

        const existingGroup = shipments.findIndex(
          // @ts-expect-error - TS2339: Property 'destination_id' does not exist on type 'never'.
          x => x.destination_id === destination_id && x.origin_id === origin_id,
        );
        const daysToShip = DateUtility.difference(new Date(), shipment.ETD);
        let difference = DateUtility.difference(new Date(), shipment.endDate);

        if (DateUtility.isDateAfter(shipment.startDate, new Date())) {
          difference = DateUtility.difference(
            shipment.startDate,
            shipment.endDate,
          );
        }

        const newShipment = {
          ...shipment,
          ETD: shipment.startDate,
          ETA: shipment.endDate,
          className: shipment.from_prediction ? 'dim' : null,
          title: shipment.received_state
            ? 'Shipment in Receiving'
            : difference > 0
              ? `${difference} Days Until Arrival`
              : 'Arriving Soon',
          link: shipment.from_prediction
            ? '#'
            : shipment.status === 'prediction'
              ? `/app/scm/shipments/create/?origin_id=${origin_id}&destination=${destination_id}&type=${shipment.origin_type}&step=1`
              : `/app/scm/shipments/${shipment._id}`,
          titleRenderer: shipment.from_prediction ? (
            <span>Projected Shipment Timeline</span>
          ) : shipment.status === 'prediction' ? (
            <a
              target="_blank"
              href={`/app/scm/shipments/create/?origin_id=${origin_id}&destination=${destination_id}&type=${shipment.origin_type}&step=1`}
              rel="noreferrer"
            >
              {daysToShip > 0
                ? `Create Shipment in ${CommonUtility.numberWithCommas(
                    daysToShip,
                  )} Days`
                : 'Create Shipment Today'}
            </a>
          ) : (
            ''
          ),
        };

        if (difference < 0) {
          newShipment.uniqueColor = red;
          newShipment.ETA = new Date();
          newShipment.title = (
            <div className="sk:flex sk:items-center">
              <Warning className="sk:mr-1" size={18} /> Shipment Overdue
            </div>
          );
        }

        if (newShipment.status === 'receiving' && newShipment.qty === 0) {
          newShipment.infoContent = (
            <DarkPopup trigger={<Info className="icon" size={15} />}>
              Awaiting for Amazon to Close Shipment, All Items Have Been
              Received
            </DarkPopup>
          );
        }

        if (existingGroup > -1) {
          // @ts-expect-error - TS2339: Property 'shipments' does not exist on type 'never'.
          shipments[existingGroup].shipments.push(newShipment);
          // @ts-expect-error - TS2339: Property 'isPredictionOnly' does not exist on type 'never'.
          shipments[existingGroup].isPredictionOnly = false;
        } else {
          let origin_name =
            shipment.origin_details?.name || shipment.po_display_id;

          if (Array.isArray(shipment.origin_details)) {
            origin_name = shipment.origin_details.map(x => x.name).join(', ');
          }

          // @ts-expect-error - TS2345: Argument of type '{ destination_id: any; origin_id: any; origin_name: any; desti...
          shipments.push({
            destination_id,
            origin_id,
            origin_name,
            destination_name: shipment.destination_details?.name,
            isPredictionOnly: !!shipment.from_prediction,
            shipments: [newShipment],
          });
        }

        if (!minDate) {
          minDate = shipment.startDate;
          maxDate = shipment.endDate;
        }

        // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
        if (DateUtility.isDateAfter(minDate, shipment.startDate))
          minDate = shipment.startDate;

        // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
        if (DateUtility.isDateAfter(shipment.endDate, maxDate)) {
          maxDate = shipment.endDate;
        }
      }
    });

    shipments.forEach(shipment => {
      const _shipment = shipment;
      let filterShipments = [];
      // @ts-expect-error - TS2339: Property 'shipments' does not exist on type 'never'.
      _shipment.shipments.forEach(sShipment => {
        if (sShipment.ETA && sShipment.ETD) {
          // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
          filterShipments.push(sShipment);
          // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
          if (DateUtility.isDateAfter(minDate, sShipment.ETD))
            minDate = sShipment.ETD;

          // @ts-expect-error - TS2345: Argument of type 'null' is not assignable to parameter of type 'DateType'.
          if (DateUtility.isDateAfter(sShipment.ETA, maxDate)) {
            maxDate = sShipment.ETA;
          }
        }
      });
      filterShipments = filterShipments.sort((shipment1, shipment2) =>
        DateUtility.isDateAfter(
          // @ts-expect-error - TS2339: Property 'ETD' does not exist on type 'never'.
          new Date(shipment1.ETD),
          // @ts-expect-error - TS2339: Property 'ETD' does not exist on type 'never'.
          new Date(shipment2.ETD),
        )
          ? 1
          : -1,
      );
      // @ts-expect-error - TS2339: Property 'shipments' does not exist on type 'never'.
      _shipment.shipments = filterShipments;

      if (filterShipments.length > 0) {
        arr.push(_shipment);
      }
    });

    arr = arr.sort((shipment1, shipment2) => {
      if (
        // @ts-expect-error - TS2339: Property 'shipments' does not exist on type 'never'.
        shipment2.shipments.length === shipment1.shipments.length &&
        // @ts-expect-error - TS2339: Property 'origin_name' does not exist on type 'never'.
        shipment1.origin_name &&
        // @ts-expect-error - TS2339: Property 'origin_name' does not exist on type 'never'.
        shipment2.origin_name
      ) {
        // @ts-expect-error - TS2339: Property 'origin_name' does not exist on type 'never'.
        return shipment1.origin_name.localeCompare(shipment2.origin_name)
          ? 1
          : -1;
      }
      // @ts-expect-error - TS2339: Property 'shipments' does not exist on type 'never'.
      return shipment2.shipments.length - shipment1.shipments.length;
    });

    arr.forEach(_shipment => {
      // @ts-expect-error - TS2339: Property 'origin_id' does not exist on type 'never'.
      userValue[`${_shipment.origin_id}-${_shipment.destination_id}`] = {
        checked: true,
        // @ts-expect-error - TS2339: Property 'shipments' does not exist on type 'never'.
        childrens: _shipment.shipments.map(x => ({
          _id: x._id,
          ETA: x.ETA,
          ETD: x.ETD,
          depOn: x.endConnect || x.destination_id,
        })),
        expanded: false,
      };
    });
    setChecked(userValue);
    setMinDate(minDate);
    setFilteredShipments(arr);
    setMaxDate(maxDate);
  }, [data]);

  return (
    <div className="sk:relative">
      <div
        className="sk:relative sk:z-120"
        style={{ width: differenceInDates * DayWidth + 208 }}
      >
        {filteredShipments?.length === 0 ? (
          <div />
        ) : (
          <HeadingContainer
            className={cn(
              'sk:flex sk:items-center',
              onlyShipments && 'sk:-mt-10',
            )}
          >
            {showShipmentCheckbox ? (
              <Checkbox
                aria-label="Include all shipments"
                className="sk:ml-2 sk:pl-1"
                isSelected={allChecked}
                onChange={() => toggleAll()}
              />
            ) : (
              <div className="sk:pl-2" />
            )}
            <h3 className="sk:m-0 sk:ml-4">Shipments</h3>
          </HeadingContainer>
        )}
      </div>
      {filteredShipments?.map((shipment, i) => (
        // @ts-expect-error - TS2739: Type '{ shipmentUserValue: any; name: string; isPredictionOnly: any; toggleCheck...
        <ShipmentRowRenderer
          key={shipment?._id || `shipment-${i}`}
          name={`${shipment?.origin_name} to ${shipment?.destination_name}`}
          isPredictionOnly={shipment.isPredictionOnly}
          toggleCheck={value =>
            toggleCheck(
              `${shipment.origin_id}-${shipment.destination_id}`,
              value,
            )
          }
          checked={
            checked[`${shipment.origin_id}-${shipment.destination_id}`].checked
          }
          toggleExpand={value =>
            toggleExpand(
              `${shipment.origin_id}-${shipment.destination_id}`,
              value,
            )
          }
          expanded={
            checked[`${shipment.origin_id}-${shipment.destination_id}`].expanded
          }
          bars={shipment?.shipments}
          markRendered={() => {}}
          origin_id={shipment?.origin_id}
          destination_id={shipment?.destination_id}
          last={i === filteredShipments.length - 1}
          selectAll={toggleAll}
          context="shipment"
          index={i}
          showShipmentCheckbox={showShipmentCheckbox}
          differenceInDates={differenceInDates}
          DayWidth={DayWidth}
          {...props}
          shipmentUserValue={checked}
        />
      ))}
    </div>
  );
}
